import { Box, Typography, Tooltip, Autocomplete, Stack, Select, TextField as MuiTextField, MenuItem, 
  FormControl, FormHelperText, Link } from "@mui/material"
import { ListPaginationContext } from "ra-core"
import { ListPaginationContextValue } from "ra-core/esm/controller/ListPaginationContext"
import { FC, useState, useMemo, ReactElement, ReactNode } from "react"
import { useHasPermit } from "@/authProvider/useHasPermit"
import Scrollbar from "@/components/Scrollbar"
import { FunctionField } from "@/components/field"
import { Datagrid } from "@/components/list/datagrid"
import { displayInferredType } from "@/components/settings/encryption/obfsRuleCompatibleMapping"
import { EDIT_DATA_CLASSIFICATION_ENCRYPTION } from "@/constants"
import SelectiveContextProvider from "@/controller/SelectiveContextProvider"
import { RecPatch } from "@/dataProvider/tools"
import { FileEncryptionTemplateDto, FileColumn, FileTemplateKind, ObfuscationDataType, ObfuscationRuleType, Encryptor } from "@/gen/api/encrypt"
import { SecondaryClassificationResponseDto } from "@/gen/api/governance"
import { OptionsData, MappingEditParams, FileDataType, ParquetFileDataType } from "./FileTemplateDetail.hooks"
import { Pagination as DefaultPagination } from '@/components/list/pagination'


export const TabularDataFileMappingViewer : FC<{
  record:FileEncryptionTemplateDto|undefined,
  optionsData:OptionsData,
  mappingEditParams:MappingEditParams,
}> = (props) => {
  const { record } = props
  return (
    <TabularDataFileMappingViewerBase {...props}
      fields={[
        <FunctionField source="columnName" label="Column Name" sortable={false} 
          key="tdfm1"
          render={(d?:FileColumn&RecPatch) => {
            if (!d) { return null }
            const nameText = 
              <Box sx={{ maxWidth: 240, cursor: d.description ? 'pointer' : undefined }}>
                <Typography variant="body2" sx={{ wordWrap: 'break-word' }}>
                  {d.displayName ? d.displayName : d.columnName}
                  {d.displayName && <>{" "}<Typography variant="caption" color="InactiveCaptionText">({d.columnName})</Typography></>}
                </Typography>
              </Box>
            return (
              d.description ? <Tooltip title={d.description} >{nameText}</Tooltip> : nameText
            )
          }}
        />,
        (record?.kind === FileTemplateKind.FixedWidth &&
          <FunctionField source="startPosition" label="Start At & Length" sortable={false} 
            key="tdfm2"
            render={(d?:FileColumn&RecPatch) => (
              d && 
              <Typography variant="body2">{d.startPosition} : {d.length}</Typography>
            )}
          />)
      ]}
    />
  )
}


export const EDIDataFileMappingViewer : FC<{
  record:FileEncryptionTemplateDto|undefined,
  optionsData:OptionsData,
  mappingEditParams:MappingEditParams,
}> = (props) => {
  const { record } = props
  return (
    <TabularDataFileMappingViewerBase {...props}
      fields={[
        <FunctionField source="columnName" label="Name" sortable={false} 
          key="edfm1"
          render={(d?:FileColumn&RecPatch) => {
            if (!d) { return null }
            const nameText = 
              <Box sx={{ maxWidth: 240, cursor: d.description ? 'pointer' : undefined }}>
                <Typography variant="body2" sx={{ wordWrap: 'break-word' }}>
                  {d.ediRule?.name}
                </Typography>
              </Box>
            return (
              d.description ? <Tooltip title={d.description} >{nameText}</Tooltip> : nameText
            )
          }}
        />,
        <FunctionField source="S.A.E" sortable={false} 
          key="edfm2"
          label={
            <Tooltip title="Segment | Anchor | Element">
              <div>S | A | E</div>
            </Tooltip>
          } 
          render={(d?:FileColumn&RecPatch) => {
            if (!d?.ediRule) { return null }
            const ediRule = d.ediRule
            
            if (!ediRule.precedingSegment) {
              return (
                <Typography variant="caption">
                  {d.ediRule.segment} |{
                    !d.ediRule.anchor ? " " :
                      ` ${d.ediRule.anchor}=${d.ediRule.anchorValue} `
                  }| {d.ediRule.element}
                </Typography>
              )
            }
            return (
              <>
                <Typography variant="caption" component="p">
                  <b>S:</b> {d.ediRule.segment}
                </Typography>
                <Typography variant="caption" component="p">
                  <b>A:</b> <br/>
                  <Typography variant="caption" color="InactiveCaptionText">preceding:</Typography><br/>
                  "{d.ediRule.precedingSegment}"{
                    !d.ediRule.precedingAnchor ? " " :
                      `> ${d.ediRule.precedingAnchor}=${d.ediRule.precedingAnchorValue} `}
                  {d.ediRule.anchor &&
                    <>
                      <br/>
                      <Typography variant="caption" color="InactiveCaptionText">self:</Typography> {` ${d.ediRule.anchor}=${d.ediRule.anchorValue} `}
                    </>
                  }
                </Typography>
                <Typography variant="caption" component="p">
                  <b>E:</b> {d.ediRule.element}
                </Typography>
              </>
            )
          }}
        />,
        <FunctionField source="startPosition" label="Start & Length" sortable={false} 
          key="edfm3"
          render={(d?:FileColumn&RecPatch) => (
            d?.ediRule?.start != null && 
            <Typography variant="caption">
              {d.ediRule.start} : {
                d.length ?? 
                  <Typography variant="caption" color="InactiveCaptionText">end</Typography>
              }
            </Typography>
          )}
        />,
      ]}
    />
  )
}



const TabularDataFileMappingViewerBase : FC<{
  record:FileEncryptionTemplateDto|undefined,
  optionsData:OptionsData,
  mappingEditParams:MappingEditParams,
  fields:ReactNode[],
}> = ({ record, optionsData, mappingEditParams, fields }) => {
  const hasWritePermit = useHasPermit([EDIT_DATA_CLASSIFICATION_ENCRYPTION])
  
  const { classifications, clssMap, ruleMap, parquetRuleMap, obfsRules } = optionsData
  const { listCtx, valueLens } = mappingEditParams

  const [pageStates, setPageStates] = useState({
    page: 1, // 1 based
    perPage: 50,
  })

  const [pagingEnabled, idFragments] = useMemo(() => {
    const ids : string[] =listCtx.ids 
    const fragments = ids.reduce((acc, id) => {
      let arr : string[] = acc.length > 0 ? acc[acc.length - 1] : [] as any
      arr = arr.length < 50 ? arr : []
      if (arr.length === 0) { acc.push(arr) }
      arr.push(id)
      return acc
    }, [] as string[][])
    return [ids.length > 50, fragments]
  }, [listCtx])

  const {pagingCtxVal, pagedListCtx} = useMemo(() => {
    const setPage = (n:number) => { setPageStates(v => ({...v, page: n})) }
    const setPerPage = (n:number) => {} // not support set perPage for now
    const pagingCtxVal : ListPaginationContextValue = {
      page: pageStates.page,
      perPage: 50,
      setPage,
      setPerPage,
      loading: false,
      total: listCtx.total,
      resource: '',
    }
    const idsOfPage = idFragments[pageStates.page - 1] ?? []
    const pagedListCtx = {
      ...listCtx,
      ids: idsOfPage,
    }
    return {pagingCtxVal, pagedListCtx}
  }, [listCtx, pageStates, idFragments])

  const classificationSelectOptions = useMemo(() => {
    return [
      { text: '[None]', value: null, },
      ...classifications,
    ]
  }, [classifications])
  
  if (!record) { return null }

  const editorFields = [
    <FunctionField 
      key="ff1"
      label={<Box component="span" sx={{ width: 200, display: 'inline-block' }}>Classification</Box>}
      sortable={false}
      render={(d?: FileColumn&RecPatch) => {
        if (!d) { return null}
        const name = d.columnName as string
        const value = valueLens.getValue(name, 'secondaryClassificationId')
        
        return (
          <SelectiveContextProvider ProviderComponent={NoopProvider} useContextFn={NoopUseCtxFn} 
            selector={()=>[record, d, hasWritePermit, classifications, clssMap, value]}
          >
            <Autocomplete 
              disabled={!hasWritePermit}
              disableClearable={true}
              options={classificationSelectOptions}
              getOptionLabel={(option) => option ? (option.text || '') : ''}
              value={clssMap[value || ''] || ''}
              
              renderInput={((params) =>
                <MuiTextField 
                  {...params} 
                  fullWidth 
                  size="small" 
                  {...{
                    inputProps: {
                      ...params.inputProps,
                      'aria-label': 'Classification',
                    }
                  }}
                />
              )}
              onChange={(event, value, reason) => {
                if (hasWritePermit && reason === 'selectOption') {
                  if ((value as any)?.value === null) { // choosed '[None]'
                    valueLens.setValues(name, {
                      secondaryClassificationId: '_rm_' as any,
                      primaryClassification: undefined,
                      encryptionRuleId: 'skip' as any,
                      obfuscationRuleId: 'skip' as any,
                    })
                  } else {
                    const cls1 = value as SecondaryClassificationResponseDto
                    const patchVal = {
                      secondaryClassificationId: cls1.secondaryClassificationId,
                      primaryClassification: cls1.primaryClassification,
                    }
                    valueLens.setValues(name, patchVal)
                  }
                }
              }}
            />
          </SelectiveContextProvider>
        )
      }}
    />,
    (record.kind !== FileTemplateKind.ParquetSample &&
    <FunctionField source="dataType" label="Data Type" sortable={false} 
      key="ff2"
      render={(d?: FileColumn&RecPatch) => {
        if (!d) { return null }
        const columnName = d.columnName || ''
        const dataType  = valueLens.getValue(columnName, 'dataType')
        const precision = valueLens.getValue(columnName, 'precision')
        const scale     = valueLens.getValue(columnName, 'scale')

        return (
          <SelectiveContextProvider ProviderComponent={NoopProvider} useContextFn={NoopUseCtxFn} 
            selector={()=>[record, d, hasWritePermit, dataType, precision, scale]}
          >
            <Warbler render={() => {
              return (
                <Stack direction="column" sx={{ width: 150 }}>
                  <Select size="small" sx={{ mr: 1, width: 120 }} 
                    disabled={!hasWritePermit}
                    value={dataType || ''}
                    onChange={(e) => {  
                      if (!hasWritePermit) { return }
                      const value = e.target.value as FileDataType
                      valueLens.setValues(columnName, {
                        dataType: value,
                        encryptionRuleId: undefined,
                        obfuscationRuleId: undefined,
                        ...(value === d.dataType && {
                          precision: d.precision as any,
                          scale: d.scale as any,
                        }),
                        ...(value !== d.dataType && {
                          precision: value !== FileDataType.Numeric ? 0
                            : record.kind === FileTemplateKind.CSV ? 7
                            : d.length ?? 7,
                          scale: 0,
                        }),
                      })
                    }}
                  >
                    <MenuItem value={FileDataType.String}  >string</MenuItem>
                    <MenuItem value={FileDataType.Numeric} >numeric</MenuItem>
                    <MenuItem value={FileDataType.Date}    >date</MenuItem>
                  </Select>
                  {dataType === FileDataType.Numeric &&
                    <Stack direction="row" alignItems="center"
                      sx={{
                        mt: 1,
                        '& .MuiTextField-root': { 
                          width: 32,
                          mx: 0.5,
                          '& input': { textAlign: 'center', py: 0 }
                        },
                      }}
                    >
                      <Typography variant="caption" color="InactiveCaptionText">(</Typography>
                      <Tooltip title="Precision">
                        <MuiTextField variant="standard" value={precision} 
                          disabled={!hasWritePermit}
                          onChange={(e) => {
                            const str = e.target.value
                            if (!str) { valueLens.setValue(columnName, 'precision', ''); return }
                            const value = Number(str)
                            if (isNaN(value) || !Number.isInteger(value) || value < 1
                            || value > (d.length || 19)) { return }
                            valueLens.setValue(columnName, 'precision', value)
                          }} 
                          onBlur={() => {
                            const value = valueLens.getValue(columnName, 'precision')
                            if (value === '') { valueLens.setValue(columnName, 'precision', 1) }
                          }} 
                        />
                      </Tooltip>
                      <Typography variant="caption" color="InactiveCaptionText">,</Typography>
                      <Tooltip title="Scale">
                        <MuiTextField variant="standard" value={scale} 
                          disabled={!hasWritePermit}
                          onChange={(e) => {
                            const str = e.target.value
                            if (!str) { valueLens.setValue(columnName, 'scale', 0); return }
                            const value = Number(str)
                            if (isNaN(value) || !Number.isInteger(value)) { return }
                            valueLens.setValue(columnName, 'scale', value)
                          }} 
                        />
                      </Tooltip>
                      <Typography variant="caption" color="InactiveCaptionText">)</Typography>
                    </Stack>
                  }
                </Stack>
              )
            }} />
          </SelectiveContextProvider>
        )
      }}
    />
    ),
    (record.kind === FileTemplateKind.ParquetSample &&
    <FunctionField source="parquetDataType" label="Data Type" sortable={false} 
      key="ff2"
      render={(d?: FileColumn&RecPatch) => {
        if (!d) { return null }
        const columnName = d.columnName || ''
        const parquetDataType = valueLens.getValue(columnName, 'parquetDataType')
        const precision = valueLens.getValue(columnName, 'precision')
        const scale     = valueLens.getValue(columnName, 'scale')

        return (
            <Warbler render={() => {
              return (
                <Stack direction="column" sx={{ width: 120 }}>
                  <Box sx={{ width: 120 }}>
                    <Typography variant="body2">{
                      parquetDataType === ParquetFileDataType.String ? 'String' : 
                      parquetDataType === ParquetFileDataType.Int16 ? 'Int16' :
                      parquetDataType === ParquetFileDataType.Int32 ? 'Int32' :
                      parquetDataType === ParquetFileDataType.Int64 ? 'Int64' :
                      parquetDataType === ParquetFileDataType.DateTime ? 'DateTime' :
                      parquetDataType === ParquetFileDataType.Single ? 'Single' :
                      parquetDataType === ParquetFileDataType.Double ? 'Double' :
                      parquetDataType === ParquetFileDataType.Decimal ? 'Decimal' :
                      parquetDataType === ParquetFileDataType.Boolean ? 'Boolean' :
                      parquetDataType === ParquetFileDataType.ByteArray ? 'Byte[]' :
                      parquetDataType === ParquetFileDataType.TimeSpan ? 'TimeSpan' :
                      parquetDataType === ParquetFileDataType.SByte ? 'Int8' :
                      parquetDataType === ParquetFileDataType.UInt16 ? 'UInt16' :
                      parquetDataType === ParquetFileDataType.UInt32 ? 'UInt32' :
                      parquetDataType === ParquetFileDataType.UInt64 ? 'UInt64' :
                      parquetDataType === ParquetFileDataType.Byte ? 'UInt8' :
                      ''}
                    </Typography>
                  </Box>
                  {parquetDataType === ParquetFileDataType.Decimal &&
                  <Stack direction="row" alignItems="center"
                    sx={{
                      mt: 1,
                      '& .MuiTextField-root': { 
                        width: 32,
                        mx: 0.5,
                        '& input': { textAlign: 'center', py: 0 }
                      },
                    }}
                  >
                    <Typography variant="caption" color="InactiveCaptionText">(</Typography>
                    <Tooltip title="Precision">
                      <MuiTextField variant="standard" value={precision} 
                        disabled={true}
                      />
                    </Tooltip>
                    <Typography variant="caption" color="InactiveCaptionText">,</Typography>
                    <Tooltip title="Scale">
                      <MuiTextField variant="standard" value={scale} 
                        disabled={true}
                      />
                    </Tooltip>
                    <Typography variant="caption" color="InactiveCaptionText">)</Typography>
                  </Stack>
                  }
                </Stack>
              )
            }} />
        )
      }}
    />
    ),
    <FunctionField source="encryptionRuleId" label="Encryption Rule" sortable={false} 
      key="ff3"
      render={(d?:FileColumn&RecPatch) => {
        if (!d) { return null }
        const columnName = d.columnName || ''
        const dataType  = record.kind === FileTemplateKind.ParquetSample ? valueLens.getValue(columnName, 'parquetDataType') : valueLens.getValue(columnName, 'dataType')
        const value     = valueLens.getValue(columnName, 'encryptionRuleId')
        return (
          <SelectiveContextProvider ProviderComponent={NoopProvider} useContextFn={NoopUseCtxFn} 
            selector={()=>[record, d, hasWritePermit, ruleMap, parquetRuleMap, dataType, value]}
          >
            <Warbler render={() => {
              let rules = record.kind === FileTemplateKind.ParquetSample ? (parquetRuleMap[dataType || '-1'] || []) : (ruleMap[dataType || '-1'] || [])
              rules =
                record.kind === FileTemplateKind.EDI && dataType === FileDataType.String 
                ? rules.filter(r => r.encryptor !== Encryptor.String && r.encryptor !== Encryptor.XML) // exclude 'Regular Text'/'XML'
                : rules
              const encryptor = rules.find(r => r.id === value)
              return (
                <>
                  <Select
                    disabled={!hasWritePermit}
                    value={value || 'skip'}
                    onChange={(e) => valueLens.setValue(columnName, 'encryptionRuleId', (e.target.value) as any)}
                    size="small"
                    sx={{ minWidth: 80 }}
                  >
                    <MenuItem value="skip">Skip</MenuItem>
                    {rules.map(r => (
                      <MenuItem key={r.id} value={r.id as any}>{r.name}</MenuItem>
                    ))}
                  </Select>
                  {encryptor &&
                    <Box sx={{ display: 'flex', flexDirection: 'column', mt: 1 }}>
                      <Typography variant="caption" sx={{ color: 'primary.light', '& span': { width: 44, display: 'inline-block' } }}>
                        <span>Input: </span>{encryptor.sampleInput}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'success.main' }}>Output: {encryptor.sampleOutput}</Typography>
                    </Box>}
                </>
              )
            }} />
          </SelectiveContextProvider>
        )
      }}
    />,
    <FunctionField source="obfuscationRuleId" label="Obfuscation Rule" sortable={false} 
      key="ff4"
      render={(d?:FileColumn&RecPatch) => {
        if (!d) { return null }
        const columnName = d.columnName || ''
        const dataType  = record.kind === FileTemplateKind.ParquetSample ? valueLens.getValue(columnName, 'parquetDataType') : valueLens.getValue(columnName, 'dataType')
        const precision = valueLens.getValue(columnName, 'precision')
        const scale     = valueLens.getValue(columnName, 'scale')
        const value     = valueLens.getValue(columnName, 'obfuscationRuleId')

        return (
          <SelectiveContextProvider ProviderComponent={NoopProvider} useContextFn={NoopUseCtxFn} 
            selector={()=>[record, d, hasWritePermit, obfsRules, dataType, precision, scale, value]}
          >
            <Warbler render={() => {
              const obfsDataType = record.kind === FileTemplateKind.ParquetSample ? 
                  (dataType === ParquetFileDataType.String ? ObfuscationDataType.String
                  : dataType === ParquetFileDataType.DateTime ? ObfuscationDataType.DateTime
                  : (dataType === ParquetFileDataType.Int16 || dataType === ParquetFileDataType.Int32 || dataType === ParquetFileDataType.Int64
                   || dataType === ParquetFileDataType.UInt16 || dataType === ParquetFileDataType.UInt32 || dataType === ParquetFileDataType.UInt64 
                  ) ? ObfuscationDataType.Integer
                  : (dataType === ParquetFileDataType.Single || dataType === ParquetFileDataType.Double || dataType === ParquetFileDataType.Decimal) ? ObfuscationDataType.Numeric
                  : undefined
                  ) 
                : (dataType === FileDataType.String ? ObfuscationDataType.String
                  : dataType === FileDataType.Date   ? ObfuscationDataType.DateTime
                  : dataType === FileDataType.Numeric ? 
                    scale === 0 ? ObfuscationDataType.Integer : ObfuscationDataType.Numeric
                  : undefined
                  )
              let rules = obfsDataType != null ? (obfsRules.maps[obfsDataType] ?? []) : []
              const currRule = value ? obfsRules.all.find(r => r.ruleId === value) : undefined

              if (obfsDataType != null) {
                switch (obfsDataType) {
                  case ObfuscationDataType.DateTime: { break; }
                  case ObfuscationDataType.String: { 
                    if (record.kind === FileTemplateKind.CSV) { break; }
                    rules = rules.filter(r => r.ruleType === ObfuscationRuleType.ClearData 
                      || r.inferredType!.length! <= (d.length ?? Infinity))
                    break; 
                  }
                  case ObfuscationDataType.Integer: { 
                    const p = (precision ?? 0) as number
                    const l = 
                        p > 10 ? 8
                      : p > 7  ? 4
                      : p > 5  ? 3
                      : p > 3  ? 2
                      : 1
                    if(record.kind !== FileTemplateKind.ParquetSample){
                      rules = rules.filter(r => r.ruleType === ObfuscationRuleType.ClearData
                        || r.inferredType!.length! <= l)
                    } else {
                      const pl = 
                          (dataType === ParquetFileDataType.Int16 || dataType === ParquetFileDataType.UInt16) ? 2
                        : (dataType === ParquetFileDataType.Int32 || dataType === ParquetFileDataType.UInt32) ? 4
                        : (dataType === ParquetFileDataType.Int64 || dataType === ParquetFileDataType.UInt64) ? 8
                        : 1
                      rules = rules.filter(r => r.ruleType === ObfuscationRuleType.ClearData
                        || r.inferredType!.length! <= pl)
                    }
                    break; 
                  }
                  case ObfuscationDataType.Numeric: { 
                    const p = (precision ?? 0) as number
                    const s = (scale ?? 0) as number
                    if(record.kind !== FileTemplateKind.ParquetSample){
                      rules = rules.filter(r => r.ruleType === ObfuscationRuleType.ClearData
                        || (r.inferredType!.p! <= p && r.inferredType!.s! <= s))
                    } else {
                      const pp = 
                          dataType === ParquetFileDataType.Single ? 7
                        : dataType === ParquetFileDataType.Double ? 16
                        : dataType === ParquetFileDataType.Decimal ? 38
                        : 38
                      rules = rules.filter(r => r.ruleType === ObfuscationRuleType.ClearData
                        || (r.inferredType!.p! <= pp))
                    }
                    break; 
                  }
                }
              }
              let error = ''
              if (value && value !== 'skip') {
                error =
                    !currRule                          ? 'Rule has been deleted'
                  : currRule.ruleType === ObfuscationRuleType.ClearData ? ''
                  : currRule.dataType !== obfsDataType ? 'DataType has been changed'
                  : !rules.includes(currRule)          ? 'Previous selection\'s range check failed'
                  : ''
              }

              return (
                <>
                  <FormControl error={Boolean(error)}>
                    <Select
                      disabled={!hasWritePermit}
                      value={value || 'skip'}
                      onChange={(e) => valueLens.setValue(columnName, 'obfuscationRuleId', (e.target.value) as any)}
                      size="small"
                      sx={{ minWidth: 80 }}
                    >
                      <MenuItem value="skip">Skip</MenuItem>
                      {error && currRule && <MenuItem value={currRule.ruleId}>{currRule.ruleName}</MenuItem>}
                      {rules.map(r => (
                        <MenuItem key={r.ruleId} value={r.ruleId} >{r.ruleName}</MenuItem>))}
                    </Select>
                    {error && <FormHelperText error>{error}</FormHelperText>}
                    {!error && currRule?.ruleType !== ObfuscationRuleType.ClearData &&
                      <FormHelperText>{displayInferredType(currRule?.inferredType as any ?? null)}</FormHelperText>}
                    {currRule?.description &&
                      <Tooltip title={currRule?.description}>
                        <Link sx={{ ml:1.75, cursor:'pointer' }}><Typography component="p" variant="caption">Read Description</Typography></Link>
                      </Tooltip>}
                  </FormControl>
                </>
              )
            }} />
          </SelectiveContextProvider>
        )
      }}
    />
  ]
  return (
    <>
      <Scrollbar options={{ suppressScrollY: true }}>
        <Box sx={{ minWidth: 1150 }}>
          <Datagrid hasBulkActions={false} {...(pagingEnabled ? pagedListCtx : listCtx)}
            sx={{ 
              '& th' : { py: 2 },
              '& td' : { py: 2 },
            }}
            children={[...fields, editorFields]}
          />
        </Box>
      </Scrollbar>
      {pagingEnabled &&
        <ListPaginationContext.Provider value={pagingCtxVal}>
          {//@ts-ignore
            <DefaultPagination rowsPerPageOptions={[]}/>}
        </ListPaginationContext.Provider>}
    </>
  )
}

const NoopProvider : FC<{value:any}> = ({children}) => <>{children}</>
const NoopUseCtxFn = () => {}
const Warbler : FC<{render:()=>ReactElement}> = ({render}) => {
  return render()
}
